import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import ColorPicker from "@/app/report/components/setting/ColorPicker"
import { colors } from "@/components/colors"
import { usePdfDataContext } from "@/app/export/pdf/contexts/PdfDataContext"
import {
  getTemplateScatterCharts,
  resolveTemplateScatterChart,
} from "@/app/report/common/sections/scatter"

import type { ChartOptionsPanelProps } from "./types"
import { AxisMetricRow } from "./SharedWidgets"

/**
 * Scatter 옵션 — X·Y 지표와 색. 처음 추가될 때 템플릿 Scatter 차트에서 가져오고 여기서 바꾼다.
 * 수동 스케일은 템플릿 차트 설정을 따른다 (#75).
 */
export default function ScatterOptions(props: ChartOptionsPanelProps) {
  const { t } = useTranslation()
  const { item, onOptionChange, metricOptions, translateKey } = props
  const { reportTemplate } = usePdfDataContext()

  const templateCharts = useMemo(() => getTemplateScatterCharts(reportTemplate), [reportTemplate])
  const currentChart = useMemo(
    () =>
      resolveTemplateScatterChart(
        templateCharts,
        item.dataSource.sectionIndex,
        item.dataSource.chartIndex,
      ),
    [templateCharts, item.dataSource.sectionIndex, item.dataSource.chartIndex],
  )

  const xMetric = item.options.xMetric ?? currentChart?.settings.xAxis ?? "totalDistance"
  const yMetric = item.options.yMetric ?? currentChart?.settings.yAxis ?? "maxSpeed"
  const color = item.options.colors?.[0] ?? currentChart?.settings.color ?? colors.blue[700]
  // 축 최대값 — 아이템 스냅샷(지표 키, null 은 자동) > 템플릿 수동 스케일 (2026-10-01 결정)
  const scaleFor = (metricKey: string, axis: "x" | "y"): number | null => {
    const entry = item.options.metricScales?.[metricKey]
    if (entry !== undefined) {
      return entry
    }
    const tpl = currentChart?.settings
    if (!tpl) {
      return null
    }
    const active = axis === "x" ? tpl.xAxisManualScaleActive : tpl.yAxisManualScaleActive
    const value = axis === "x" ? tpl.xAxisManualScaleValue : tpl.yAxisManualScaleValue

    return active && value != null ? value : null
  }

  // 제목은 고른 지표로 자동 — "X 지표 / Y 지표" (2026-10-01 요청)
  const handleMetricChange = (field: "xMetric" | "yMetric", metricKey: string) => {
    onOptionChange(field, metricKey)
    const nextX = field === "xMetric" ? metricKey : xMetric
    const nextY = field === "yMetric" ? metricKey : yMetric
    onOptionChange("title", `${translateKey(nextX) || nextX} / ${translateKey(nextY) || nextY}`)
  }

  return (
    <>
      {/* X·Y 축 — [지표 | 최대값] 한 줄씩 (2026-10-01 요청) */}
      <AxisMetricRow
        label={t("xAxis", "X Axis")}
        value={xMetric}
        metricOptions={metricOptions}
        onChange={(key) => handleMetricChange("xMetric", key)}
        scale={scaleFor(xMetric, "x")}
        onScaleChange={(value) =>
          onOptionChange("metricScales", { ...(item.options.metricScales ?? {}), [xMetric]: value })
        }
      />
      <AxisMetricRow
        label={t("yAxis", "Y Axis")}
        value={yMetric}
        metricOptions={metricOptions}
        onChange={(key) => handleMetricChange("yMetric", key)}
        scale={scaleFor(yMetric, "y")}
        onScaleChange={(value) =>
          onOptionChange("metricScales", { ...(item.options.metricScales ?? {}), [yMetric]: value })
        }
      />

      <div className="PropertyPanel__field">
        <label>{t("chartColor", "Chart Color")}</label>
        <div className="PropertyPanel__color-swatches">
          <ColorPicker color={color} setColor={(c) => onOptionChange("colors", [c])} hideLabel />
        </div>
      </div>
    </>
  )
}
